import { Badge } from "@bowfast/ui/components/badge";
import { Button } from "@bowfast/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@bowfast/ui/components/card";
import { Field, FieldError, FieldLabel } from "@bowfast/ui/components/field";
import { Input } from "@bowfast/ui/components/input";
import { Progress } from "@bowfast/ui/components/progress";
import { Separator } from "@bowfast/ui/components/separator";
import { Textarea } from "@bowfast/ui/components/textarea";
import { cn } from "@bowfast/ui/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import {
  AnchorIcon,
  ArrowLeftIcon,
  CalendarIcon,
  CheckCircle2Icon,
  FlagIcon,
  FuelIcon,
  GaugeIcon,
  InfoIcon,
  Loader2Icon,
  RadioIcon,
  RulerIcon,
  SailboatIcon,
  UsersIcon,
  WavesIcon,
} from "lucide-react";
import type { Control } from "react-hook-form";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useAuth } from "react-oidc-context";
import { SectionCard } from "~/components/vessels/SectionCard";
import { TYPE_OPTIONS, type VesselFormValues, vesselSchema } from "~/components/vessels/vessel-schema";
import type { CreateVesselInput, VesselSource } from "~/graphql/generated";
import { fadeUp, stagger } from "~/lib/animations";
import { createSdk } from "~/lib/gql";
import { useOrg } from "~/lib/org-context";

// ── Input sanitizer ─────────────────────────────────────────────────────────

function toVesselInput(values: VesselFormValues): CreateVesselInput {
  const s = (v: string | undefined) => (v !== undefined && v.trim() !== "" ? v.trim() : undefined);
  const n = (v: number | undefined) => (typeof v === "number" && !Number.isNaN(v) ? v : undefined);
  const raw: Record<string, unknown> = {
    name: values.name,
    vesselType: values.vesselType,
    source: values.source,
    make: s(values.make),
    model: s(values.model),
    yearBuilt: n(values.yearBuilt),
    homePort: s(values.homePort),
    flagState: s(values.flagState),
    registration: s(values.registration),
    callSign: s(values.callSign),
    mmsiNumber: s(values.mmsiNumber),
    lengthM: n(values.lengthM),
    beamM: n(values.beamM),
    draftM: n(values.draftM),
    enginePowerKw: n(values.enginePowerKw),
    fuelCapacityL: n(values.fuelCapacityL),
    waterCapacityL: n(values.waterCapacityL),
    berths: n(values.berths),
    maxPassengers: n(values.maxPassengers),
    charterCompanyName: s(values.charterCompanyName),
    charterCompanyContact: s(values.charterCompanyContact),
    charterStartDate: s(values.charterStartDate),
    charterEndDate: s(values.charterEndDate),
  };
  // Strip keys whose value is undefined so the GraphQL client omits them entirely
  // (undefined keys get serialized as null by some clients, which the backend rejects)
  return Object.fromEntries(Object.entries(raw).filter(([, v]) => v !== undefined)) as CreateVesselInput;
}

// ── Completion tracker ────────────────────────────────────────────────────────

function useCompletion(control: Control<VesselFormValues>) {
  const [name, vesselType, homePort, berths, lengthM, source, charterCompanyName, charterStartDate, charterEndDate] =
    useWatch({
      control,
      name: [
        "name",
        "vesselType",
        "homePort",
        "berths",
        "lengthM",
        "source",
        "charterCompanyName",
        "charterStartDate",
        "charterEndDate",
      ] as const,
    }) as [string, string, string, number | undefined, number | undefined, VesselSource, string, string, string];

  const isChartered = source === "EXTERNAL_CHARTER";
  const checks = [
    { label: "Name the vessel", done: name.trim().length > 0 },
    { label: "Choose type", done: !!vesselType },
    { label: "Home port", done: homePort.trim().length > 0 },
    { label: "Set berths", done: typeof berths === "number" && berths > 0 },
    { label: "Dimensions", done: typeof lengthM === "number" && lengthM > 0 },
    ...(isChartered
      ? [
          { label: "Charter company", done: charterCompanyName.trim().length > 0 },
          { label: "Charter dates", done: charterStartDate.length > 0 && charterEndDate.length > 0 },
        ]
      : []),
  ];
  const done = checks.filter((c) => c.done).length;
  return { checks, done, total: checks.length, pct: Math.round((done / checks.length) * 100) };
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function NewVesselPage() {
  const { orgId } = useOrg();
  const auth = useAuth();
  const token = auth.user?.access_token ?? "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useForm<VesselFormValues>({
    resolver: zodResolver(vesselSchema),
    defaultValues: {
      name: "",
      source: "OWNED",
      make: "",
      model: "",
      homePort: "",
      flagState: "",
      registration: "",
      callSign: "",
      mmsiNumber: "",
      charterCompanyName: "",
      charterCompanyContact: "",
      charterStartDate: "",
      charterEndDate: "",
    },
  });

  const source = useWatch({ control: form.control, name: "source" });
  const vesselType = useWatch({ control: form.control, name: "vesselType" });
  const isChartered = source === "EXTERNAL_CHARTER";
  const completion = useCompletion(form.control);
  const [
    previewName,
    previewMake,
    previewModel,
    previewHomePort,
    previewFlagState,
    previewLengthM,
    previewBerths,
    previewCharterCo,
    previewCharterStart,
    previewCharterEnd,
  ] = useWatch({
    control: form.control,
    name: [
      "name",
      "make",
      "model",
      "homePort",
      "flagState",
      "lengthM",
      "berths",
      "charterCompanyName",
      "charterStartDate",
      "charterEndDate",
    ] as const,
  }) as [string, string, string, string, string, number | undefined, number | undefined, string, string, string];

  const mutation = useMutation({
    mutationFn: (input: CreateVesselInput) => createSdk(token).CreateVessel({ orgId, input }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["vessels", orgId] });
      navigate({ to: "/vessels/$vesselSlug", params: { vesselSlug: data.createVessel.slug } });
    },
  });

  return (
    <motion.form
      {...stagger}
      initial="initial"
      animate="animate"
      className="mx-auto w-full max-w-304 p-4 md:p-6 lg:p-8"
      onSubmit={form.handleSubmit((values) => mutation.mutate(toVesselInput(values)))}
    >
      {/* ── Page header ── */}
      <motion.div {...fadeUp} className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 gap-1.5 text-muted-foreground"
            nativeButton={false}
            render={<Link to="/vessels" />}
          >
            <ArrowLeftIcon className="size-3.5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-xl bg-linear-to-br from-shallow-500 to-abyss-500 shadow-md">
              <SailboatIcon className="size-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-[1.375rem] font-bold tracking-[-0.01em]">Add a vessel</h1>
              <p className="text-xs text-muted-foreground">Register a vessel for your fleet or an upcoming charter</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="draft" dotted>
            Draft
          </Badge>
          <Button
            size="sm"
            className="gap-1.5"
            type="submit"
            {...(completion.pct < 60 || mutation.isPending ? { disabled: true } : {})}
          >
            {mutation.isPending ? (
              <Loader2Icon className="size-3.5 animate-spin" />
            ) : (
              <CheckCircle2Icon className="size-3.5" />
            )}
            {mutation.isPending ? "Adding vessel…" : "Add vessel"}
          </Button>
        </div>
      </motion.div>

      {/* ── Two-column layout ── */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
        {/* ── Left: Form sections ── */}
        <div className="flex flex-col gap-5">
          {/* ── 1. Ownership ── */}
          <SectionCard icon={AnchorIcon} title="Ownership" delay={0.02}>
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Is this vessel part of your fleet, or chartered from an external company?
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  {
                    value: "OWNED" as VesselSource,
                    label: "Owned vessel",
                    desc: "Part of your permanent fleet",
                    Icon: AnchorIcon,
                    gradient: "from-abyss-500 to-abyss-300",
                  },
                  {
                    value: "EXTERNAL_CHARTER" as VesselSource,
                    label: "Chartered vessel",
                    desc: "Rented for a period from an external company",
                    Icon: CalendarIcon,
                    gradient: "from-shallow-500 to-shallow-300",
                  },
                ].map((opt) => {
                  const active = source === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => form.setValue("source", opt.value)}
                      className={cn(
                        "group relative flex items-start gap-3 rounded-xl border-2 p-4 text-left transition-all",
                        active
                          ? "border-abyss-500 bg-abyss-50/50 ring-1 ring-abyss-500/20 dark:bg-abyss-900/50"
                          : "border-border hover:border-abyss-300 hover:bg-muted/30",
                      )}
                    >
                      <div
                        className={cn(
                          "flex size-10 shrink-0 items-center justify-center rounded-lg bg-linear-to-br text-white shadow-sm transition-transform group-hover:scale-105",
                          active ? opt.gradient : "from-muted to-muted",
                        )}
                      >
                        <opt.Icon className={cn("size-4", active ? "text-white" : "text-muted-foreground")} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold">{opt.label}</p>
                          {active && <CheckCircle2Icon className="size-4 shrink-0 text-abyss-500" />}
                        </div>
                        <p className="mt-0.5 text-xs text-muted-foreground">{opt.desc}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </SectionCard>

          {/* ── 2. Vessel identity ── */}
          <SectionCard icon={SailboatIcon} title="Vessel identity" delay={0.06}>
            <div className="space-y-5">
              {/* Name — big input like expedition title */}
              <Controller
                name="name"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor="vessel-name" className="text-sm font-medium">
                      Vessel name
                    </FieldLabel>
                    <Input
                      {...field}
                      id="vessel-name"
                      aria-invalid={fieldState.invalid}
                      placeholder="SY Aventura II"
                      className="font-heading text-base font-semibold"
                    />
                    <FieldError errors={[fieldState.error]} />
                  </Field>
                )}
              />

              {/* Type selector — rich cards like expedition kind */}
              <Controller
                name="vesselType"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel className="text-sm font-medium">Hull type</FieldLabel>
                    <div className="grid grid-cols-3 gap-2.5">
                      {TYPE_OPTIONS.map((opt) => {
                        const active = field.value === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => field.onChange(opt.value)}
                            className={cn(
                              "flex flex-col items-center gap-2 rounded-xl border px-3 py-4 text-center transition-all",
                              active
                                ? "border-abyss-500 bg-abyss-50 text-abyss-700 shadow-sm dark:bg-abyss-900 dark:text-abyss-200"
                                : "border-border text-muted-foreground hover:border-abyss-200 hover:bg-muted/30",
                            )}
                          >
                            <opt.Icon
                              className={cn("size-5", active ? "text-abyss-500" : "text-muted-foreground/60")}
                            />
                            <span className="text-sm font-semibold">{opt.label}</span>
                          </button>
                        );
                      })}
                    </div>
                    <FieldError errors={[fieldState.error]} />
                  </Field>
                )}
              />

              <Separator />

              {/* Make, model, year */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Controller
                  name="make"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor="vessel-make" className="text-sm font-medium">
                        Make <span className="font-normal text-muted-foreground">(builder)</span>
                      </FieldLabel>
                      <Input
                        {...field}
                        id="vessel-make"
                        aria-invalid={fieldState.invalid}
                        placeholder="Hallberg-Rassy"
                      />
                      <FieldError errors={[fieldState.error]} />
                    </Field>
                  )}
                />
                <Controller
                  name="model"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor="vessel-model" className="text-sm font-medium">
                        Model
                      </FieldLabel>
                      <Input {...field} id="vessel-model" aria-invalid={fieldState.invalid} placeholder="HR 44" />
                      <FieldError errors={[fieldState.error]} />
                    </Field>
                  )}
                />
                <Field data-invalid={!!form.formState.errors.yearBuilt}>
                  <FieldLabel htmlFor="vessel-year" className="text-sm font-medium">
                    Year built
                  </FieldLabel>
                  <Input
                    {...form.register("yearBuilt", { valueAsNumber: true })}
                    id="vessel-year"
                    type="number"
                    aria-invalid={!!form.formState.errors.yearBuilt}
                    placeholder="2018"
                    min={1900}
                    max={2030}
                  />
                  <FieldError errors={[form.formState.errors.yearBuilt]} />
                </Field>
              </div>

              {/* Notes / description */}
              <div className="space-y-1.5">
                <FieldLabel className="text-sm font-medium">
                  Notes <span className="font-normal text-muted-foreground">(optional)</span>
                </FieldLabel>
                <Textarea rows={2} placeholder="Anything notable — refit history, equipment highlights, keel type…" />
              </div>
            </div>
          </SectionCard>

          {/* ── 3. Registration & maritime identity ── */}
          <SectionCard icon={FlagIcon} title="Registration & maritime identity" delay={0.1}>
            <div className="space-y-4">
              {/* Port & flag — styled blocks like departure/arrival */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-border bg-muted/20 p-4 dark:bg-abyss-900/30">
                  <p className="mb-3 flex items-center gap-1.5 text-[0.625rem] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                    <AnchorIcon className="size-3" />
                    Home port
                  </p>
                  <Controller
                    name="homePort"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <Field data-invalid={fieldState.invalid}>
                        <Input {...field} aria-invalid={fieldState.invalid} placeholder="Lisbon, Portugal" />
                        <FieldError errors={[fieldState.error]} />
                      </Field>
                    )}
                  />
                </div>
                <div className="rounded-xl border border-border bg-muted/20 p-4 dark:bg-abyss-900/30">
                  <p className="mb-3 flex items-center gap-1.5 text-[0.625rem] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                    <FlagIcon className="size-3" />
                    Flag state
                  </p>
                  <Controller
                    name="flagState"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <Field data-invalid={fieldState.invalid}>
                        <Input {...field} aria-invalid={fieldState.invalid} placeholder="Portugal" />
                        <FieldError errors={[fieldState.error]} />
                      </Field>
                    )}
                  />
                </div>
              </div>

              <Separator />

              {/* Registration, call sign, MMSI */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Controller
                  name="registration"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor="vessel-registration" className="text-sm font-medium">
                        Registration
                      </FieldLabel>
                      <Input
                        {...field}
                        id="vessel-registration"
                        aria-invalid={fieldState.invalid}
                        placeholder="LX-12345"
                      />
                      <FieldError errors={[fieldState.error]} />
                    </Field>
                  )}
                />
                <Controller
                  name="callSign"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor="vessel-callsign" className="text-sm font-medium">
                        Call sign
                      </FieldLabel>
                      <Input {...field} id="vessel-callsign" aria-invalid={fieldState.invalid} placeholder="CQAB7" />
                      <FieldError errors={[fieldState.error]} />
                    </Field>
                  )}
                />
                <Controller
                  name="mmsiNumber"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <div className="flex items-center gap-1.5">
                        <FieldLabel htmlFor="vessel-mmsi" className="text-sm font-medium">
                          MMSI
                        </FieldLabel>
                        <RadioIcon className="size-3 text-muted-foreground" />
                      </div>
                      <Input
                        {...field}
                        id="vessel-mmsi"
                        aria-invalid={fieldState.invalid}
                        placeholder="263000001"
                        maxLength={9}
                      />
                      <FieldError errors={[fieldState.error]} />
                    </Field>
                  )}
                />
              </div>

              <div className="rounded-lg border border-abyss-100 bg-abyss-50/50 px-4 py-3 dark:border-abyss-900 dark:bg-abyss-950/30">
                <div className="flex items-start gap-2">
                  <InfoIcon className="mt-0.5 size-3.5 shrink-0 text-abyss-400" />
                  <p className="text-xs text-abyss-600 dark:text-abyss-300">
                    MMSI and call sign enable AIS tracking integration. Registration appears on official crew lists and
                    certificates.
                  </p>
                </div>
              </div>
            </div>
          </SectionCard>

          {/* ── 4. Dimensions & technical ── */}
          <SectionCard icon={RulerIcon} title="Dimensions & technical" delay={0.14}>
            <div className="space-y-5">
              {/* LOA, beam, draft — with unit suffixes */}
              <div className="grid grid-cols-3 gap-3">
                {(
                  [
                    { key: "lengthM", label: "Length (LOA)", placeholder: "13.5", unit: "m" },
                    { key: "beamM", label: "Beam", placeholder: "4.2", unit: "m" },
                    { key: "draftM", label: "Draft", placeholder: "2.1", unit: "m" },
                  ] as const
                ).map((dim) => (
                  <Field key={dim.key} data-invalid={!!form.formState.errors[dim.key]}>
                    <FieldLabel htmlFor={`vessel-${dim.key}`} className="text-sm font-medium">
                      {dim.label}
                    </FieldLabel>
                    <div className="relative">
                      <RulerIcon className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="number"
                        step="0.1"
                        {...form.register(dim.key, { valueAsNumber: true })}
                        id={`vessel-${dim.key}`}
                        aria-invalid={!!form.formState.errors[dim.key]}
                        placeholder={dim.placeholder}
                        className="pl-9 pr-8"
                      />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 font-mono text-xs text-muted-foreground">
                        {dim.unit}
                      </span>
                    </div>
                    <FieldError errors={[form.formState.errors[dim.key]]} />
                  </Field>
                ))}
              </div>

              <Separator />

              {/* Engine & tanks */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Field data-invalid={!!form.formState.errors.enginePowerKw}>
                  <FieldLabel htmlFor="vessel-engine-power" className="text-sm font-medium">
                    Engine power
                  </FieldLabel>
                  <div className="relative">
                    <GaugeIcon className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="number"
                      step="0.1"
                      {...form.register("enginePowerKw", { valueAsNumber: true })}
                      id="vessel-engine-power"
                      aria-invalid={!!form.formState.errors.enginePowerKw}
                      placeholder="55"
                      className="pl-9 pr-10"
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 font-mono text-xs text-muted-foreground">
                      kW
                    </span>
                  </div>
                  <FieldError errors={[form.formState.errors.enginePowerKw]} />
                </Field>
                <Field data-invalid={!!form.formState.errors.fuelCapacityL}>
                  <FieldLabel htmlFor="vessel-fuel" className="text-sm font-medium">
                    Fuel capacity
                  </FieldLabel>
                  <div className="relative">
                    <FuelIcon className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="number"
                      {...form.register("fuelCapacityL", { valueAsNumber: true })}
                      id="vessel-fuel"
                      aria-invalid={!!form.formState.errors.fuelCapacityL}
                      placeholder="200"
                      className="pl-9 pr-6"
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 font-mono text-xs text-muted-foreground">
                      L
                    </span>
                  </div>
                  <FieldError errors={[form.formState.errors.fuelCapacityL]} />
                </Field>
                <Field data-invalid={!!form.formState.errors.waterCapacityL}>
                  <FieldLabel htmlFor="vessel-water" className="text-sm font-medium">
                    Water capacity
                  </FieldLabel>
                  <div className="relative">
                    <WavesIcon className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="number"
                      {...form.register("waterCapacityL", { valueAsNumber: true })}
                      id="vessel-water"
                      aria-invalid={!!form.formState.errors.waterCapacityL}
                      placeholder="350"
                      className="pl-9 pr-6"
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 font-mono text-xs text-muted-foreground">
                      L
                    </span>
                  </div>
                  <FieldError errors={[form.formState.errors.waterCapacityL]} />
                </Field>
              </div>
            </div>
          </SectionCard>

          {/* ── 5. Crew & berths ── */}
          <SectionCard icon={UsersIcon} title="Crew capacity" delay={0.18}>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field data-invalid={!!form.formState.errors.berths}>
                  <FieldLabel htmlFor="vessel-berths" className="text-sm font-medium">
                    Berths <span className="text-coral-500">*</span>
                  </FieldLabel>
                  <Input
                    type="number"
                    {...form.register("berths", { valueAsNumber: true })}
                    id="vessel-berths"
                    aria-invalid={!!form.formState.errors.berths}
                    placeholder="8"
                    min={1}
                  />
                  <p className="text-xs text-muted-foreground">
                    Total sleeping berths. Used for crew assignment limits.
                  </p>
                  <FieldError errors={[form.formState.errors.berths]} />
                </Field>
                <Field data-invalid={!!form.formState.errors.maxPassengers}>
                  <FieldLabel htmlFor="vessel-max-passengers" className="text-sm font-medium">
                    Max passengers <span className="font-normal text-muted-foreground">(legal capacity)</span>
                  </FieldLabel>
                  <Input
                    type="number"
                    {...form.register("maxPassengers", { valueAsNumber: true })}
                    id="vessel-max-passengers"
                    aria-invalid={!!form.formState.errors.maxPassengers}
                    placeholder="10"
                    min={1}
                  />
                  <p className="text-xs text-muted-foreground">As stated on the vessel's safety certificate.</p>
                  <FieldError errors={[form.formState.errors.maxPassengers]} />
                </Field>
              </div>
            </div>
          </SectionCard>

          {/* ── 6. Charter details (conditional) ── */}
          <AnimatePresence>
            {isChartered && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                style={{ overflow: "hidden" }}
              >
                <SectionCard icon={CalendarIcon} title="Charter details" delay={0.22}>
                  <div className="space-y-4">
                    <p className="text-xs text-muted-foreground">
                      Details of the external charter company and rental period.
                    </p>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Controller
                        name="charterCompanyName"
                        control={form.control}
                        render={({ field, fieldState }) => (
                          <Field data-invalid={fieldState.invalid}>
                            <FieldLabel htmlFor="vessel-charter-co" className="text-sm font-medium">
                              Charter company <span className="text-coral-500">*</span>
                            </FieldLabel>
                            <Input
                              {...field}
                              id="vessel-charter-co"
                              aria-invalid={fieldState.invalid}
                              placeholder="Sunsail, The Moorings…"
                            />
                            <FieldError errors={[fieldState.error]} />
                          </Field>
                        )}
                      />
                      <Controller
                        name="charterCompanyContact"
                        control={form.control}
                        render={({ field, fieldState }) => (
                          <Field data-invalid={fieldState.invalid}>
                            <FieldLabel htmlFor="vessel-charter-contact" className="text-sm font-medium">
                              Company contact
                            </FieldLabel>
                            <Input
                              {...field}
                              id="vessel-charter-contact"
                              aria-invalid={fieldState.invalid}
                              placeholder="bookings@sunsail.com"
                            />
                            <FieldError errors={[fieldState.error]} />
                          </Field>
                        )}
                      />
                    </div>

                    <Separator />

                    {/* Charter period — styled blocks */}
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="rounded-xl border border-border bg-muted/20 p-4 dark:bg-abyss-900/30">
                        <p className="mb-3 flex items-center gap-1.5 text-[0.625rem] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                          <CalendarIcon className="size-3" />
                          Charter starts
                        </p>
                        <Controller
                          name="charterStartDate"
                          control={form.control}
                          render={({ field, fieldState }) => (
                            <Field data-invalid={fieldState.invalid}>
                              <Input {...field} type="date" aria-invalid={fieldState.invalid} />
                              <FieldError errors={[fieldState.error]} />
                            </Field>
                          )}
                        />
                      </div>
                      <div className="rounded-xl border border-border bg-muted/20 p-4 dark:bg-abyss-900/30">
                        <p className="mb-3 flex items-center gap-1.5 text-[0.625rem] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                          <FlagIcon className="size-3" />
                          Charter ends
                        </p>
                        <Controller
                          name="charterEndDate"
                          control={form.control}
                          render={({ field, fieldState }) => (
                            <Field data-invalid={fieldState.invalid}>
                              <Input {...field} type="date" aria-invalid={fieldState.invalid} />
                              <FieldError errors={[fieldState.error]} />
                            </Field>
                          )}
                        />
                      </div>
                    </div>

                    <div className="rounded-lg border border-shallow-100 bg-shallow-50/50 px-4 py-3 dark:border-shallow-900 dark:bg-shallow-950/30">
                      <div className="flex items-start gap-2">
                        <WavesIcon className="mt-0.5 size-3.5 shrink-0 text-shallow-600" />
                        <p className="text-xs text-shallow-700 dark:text-shallow-300">
                          Chartered vessels are available for expedition assignment only within the charter period.
                          After the end date, the vessel moves to <strong>Retired</strong> status automatically.
                        </p>
                      </div>
                    </div>
                  </div>
                </SectionCard>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── Right: Sticky sidebar ── */}
        <div className="hidden lg:block">
          <div className="sticky top-6 space-y-5">
            {/* Completion progress */}
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.04 }}>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-[0.9375rem] font-semibold">Readiness</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {completion.done} of {completion.total} complete
                      </span>
                      <span className="font-heading font-bold text-abyss-500">{completion.pct}%</span>
                    </div>
                    <Progress value={completion.pct} className="h-2" />
                    <div className="space-y-1.5 pt-1">
                      {completion.checks.map((c) => (
                        <div key={c.label} className="flex items-center gap-2 text-xs">
                          <div
                            className={cn(
                              "flex size-4 items-center justify-center rounded-full",
                              c.done ? "bg-shallow-500 text-white" : "border border-border bg-muted",
                            )}
                          >
                            {c.done && <CheckCircle2Icon className="size-3" />}
                          </div>
                          <span className={cn(c.done ? "text-muted-foreground line-through" : "text-foreground")}>
                            {c.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Live preview card */}
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }}>
              <Card className="overflow-hidden pt-0">
                {/* Preview header */}
                <div className="border-b border-border bg-muted/30 px-5 py-4">
                  <p className="text-[0.625rem] font-bold uppercase tracking-[0.06em] text-muted-foreground">Preview</p>
                  <div className="mt-2 flex items-center gap-3">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-linear-to-br from-abyss-500 to-shallow-500 shadow-sm">
                      <SailboatIcon className="size-4 text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate font-heading text-sm font-bold">
                        {completion.checks[0]?.done ? previewName : "Untitled vessel"}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {TYPE_OPTIONS.find((t) => t.value === vesselType)?.label ?? "—"}{" "}
                        {previewMake && `· ${previewMake}`}
                        {previewModel && ` ${previewModel}`}
                      </p>
                    </div>
                  </div>
                </div>
                <CardContent className="space-y-2 pt-3">
                  {[
                    { label: "Source", value: isChartered ? "Chartered" : "Owned" },
                    { label: "Home port", value: previewHomePort || "—" },
                    { label: "Flag", value: previewFlagState || "—" },
                    { label: "LOA", value: previewLengthM ? `${previewLengthM} m` : "—" },
                    { label: "Berths", value: previewBerths || "—" },
                    ...(isChartered
                      ? [
                          { label: "Charter co.", value: previewCharterCo || "—" },
                          {
                            label: "Period",
                            value:
                              previewCharterStart && previewCharterEnd
                                ? `${previewCharterStart} → ${previewCharterEnd}`
                                : "—",
                          },
                        ]
                      : []),
                  ].map((row) => (
                    <div key={row.label} className="flex items-center justify-between gap-4">
                      <span className="text-[0.6875rem] text-muted-foreground">{row.label}</span>
                      <span className="truncate text-right text-[0.6875rem] font-medium">{row.value}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </motion.div>

            {/* After creation hint */}
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.12 }}>
              <div className="rounded-xl border border-border bg-muted/30 px-4 py-3">
                <p className="text-[0.6875rem] font-semibold text-foreground">After creation</p>
                <ul className="mt-2 space-y-1.5 text-xs text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <div className="size-1 shrink-0 rounded-full bg-muted-foreground/40" />
                    Upload photos &amp; set cover image
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="size-1 shrink-0 rounded-full bg-muted-foreground/40" />
                    Attach documents (registration, insurance, safety cert)
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="size-1 shrink-0 rounded-full bg-muted-foreground/40" />
                    Log maintenance records
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="size-1 shrink-0 rounded-full bg-muted-foreground/40" />
                    Assign to expeditions
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.form>
  );
}
